import { useCallback, useEffect, useMemo, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { searchClientsBasic } from 'services/contractGeneratorService';
import { getCasesByClientId } from 'services/caseServices';
import {
  createProcess,
  decideProcessViability,
  getFlowSettings,
} from 'services/adminNegotiationService';
import { adminNegotiationI18n } from './i18n';
import TextArea from '../../components/TextArea/TextArea';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import AsyncStateView from '../../components/AsyncStateView/AsyncStateView';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

/** Deja solo dígitos (patrón dígitos-only de WritingTypeFormModal). */
const onlyDigits = (v) => String(v ?? '').replace(/[^0-9]/g, '');

/** Formatea un string de dígitos como pesos chilenos: "1500000" → "$1.500.000". Vacío → "".
 *  Agrupa de a 3 con punto (formato CLP), sin decimales. */
const formatPesos = (v) => {
  const d = onlyDigits(v);
  if (d === '') return '';
  return `$${d.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
};

const blankCreditor = () => ({
  creditorName: '',
  debtAmount: '',
  arrearsDays: '',
});

const EXCLUDED_BUSINESS_AREAS = [2, 11]; // SAC, EEPP — misma convención inline que las listas de casos/perfil/calendario

// Estilos de la tabla de acreedores: espejo de la lista "Documentos requeridos" de
// Escrituras (WritingTypeDocumentsPanel). Inline, sin CSS global nuevo. Columnas:
// N° · Nombre · Monto · Días de mora · quitar.
const CREDITORS_CARD = {
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(16,24,40,.08),0 0 0 1px rgba(16,24,40,.06)',
  overflow: 'visible',
};
// Alto del Input del design system. Lo necesitamos explícito porque el Input reserva espacio
// bajo el campo (helper/error) aunque no lo use: centrar por 'center' alinearía respecto a esa
// caja más alta y dejaría el número de fila descolgado hacia abajo.
const CREDITORS_INPUT_H = 40;
const CREDITORS_GRID = {
  display: 'grid',
  // Pisos para que los placeholders se lean completos: "Monto de la deuda" (170px) y
  // "Días de mora" (160px). El nombre se lleva el resto.
  gridTemplateColumns:
    '28px minmax(0, 2.2fr) minmax(170px, 1fr) minmax(160px, 0.8fr) 32px',
  gap: 10,
  alignItems: 'start',
};
const CREDITORS_HDR = {
  fontSize: 11,
  fontWeight: 600,
  color: '#98a2b3',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  // Compensa el borde + padding-left del Input, para que el título quede a plomo con el
  // texto del campo que encabeza.
  paddingLeft: 13,
};
// Celda del número: misma altura que el Input y contenido centrado → queda a la misma
// altura que el texto del campo, no del bloque completo.
const CREDITORS_NUM = {
  fontSize: 12,
  color: '#98a2b3',
  height: CREDITORS_INPUT_H,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};
// Celda de la ✕: misma altura que el Input para que quede a plomo con el campo.
const CREDITORS_ACTION = {
  height: CREDITORS_INPUT_H,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Modal de proceso de Negociación Administrativa (flujo manual de viabilidad).
 *
 *   - modo `create`: se elige un cliente existente (ComboBox + búsqueda dinámica, mismo
 *     patrón de AdminNegotiationStartCaseModal), su teléfono y una lista de acreedores
 *     (nombre + monto + días de mora). Al aprobar se crea el caso + check `confirmed`.
 *   - modo `decide`: cliente fijo (viene de una tarea GC `viability_pending`); el usuario
 *     ingresa la lista de acreedores y aprueba o rechaza (con razón). Reject NO manda
 *     mensaje al cliente (solo historia).
 *
 * i18n propio (namespace NA `proc_*`), reutilizable desde Documentos y desde la
 * intercepción de aprobar/rechazar tareas. Solo design system vía prop
 * DesignSystemComponents + TextArea/ConfirmModal locales.
 */
export default function AdminNegotiationProcessModal({
  DesignSystemComponents,
  mode = 'create',
  checkId = null,
  clientLabel = null,
  initialDecision = 'approve',
  onClose,
  onCompleted,
}) {
  const { t } = useScopedTranslation(adminNegotiationI18n);
  const { Modal, Button, ComboBox, Input, IconButton } =
    DesignSystemComponents ?? {};

  const isDecide = mode === 'decide';

  // Cliente (modo create).
  const [clientSearchText, setClientSearchText] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Caso del cliente (modo create): al elegir cliente se traen sus casos elegibles (área
  // NO excluida). Con casos elegibles → el caso es obligatorio; sin casos → se permite
  // enviar y el back auto-crea el caso.
  const [caseOptions, setCaseOptions] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  const [telefono, setTelefono] = useState('');
  const [creditors, setCreditors] = useState([blankCreditor()]);
  // Mínimo de acreedores del flujo (fallback 2): en create se renderizan N filas de
  // entrada al abrir; en decide se arranca con UNA (reject ignora acreedores). No se
  // puede quitar por debajo de N una vez agregadas.
  const [minCreditors, setMinCreditors] = useState(2);
  const [decision, setDecision] = useState(
    isDecide ? initialDecision : 'approve'
  );
  const [rejectionReason, setRejectionReason] = useState('');

  // Estado async del envío (idle/loading/success/error) → body-swap del modal (AsyncStateView).
  // `sending` se conserva como alias para no reescribir los disabled= del formulario idle.
  const [status, setStatus] = useState('idle');
  const [submitError, setSubmitError] = useState(null);
  const sending = status === 'loading';
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Búsqueda dinámica de clientes (debounce 400ms, mín. 3 caracteres) — solo create.
  useEffect(() => {
    if (isDecide) return undefined;
    const deb = setTimeout(async () => {
      if (!clientSearchText || clientSearchText.length < 3) {
        setClientOptions([]);
        return;
      }
      setLoadingClients(true);
      try {
        const result = await searchClientsBasic({ text: clientSearchText });
        setClientOptions(
          (result || []).map((c) => ({
            label: c.rut ? `${c.name} (${c.rut})` : c.name,
            value: c.id,
            clientId: c.clientId ?? c.id,
            phone: c.telephone ?? c.phone ?? '',
          }))
        );
      } catch {
        setClientOptions([]);
      } finally {
        setLoadingClients(false);
      }
    }, 400);
    return () => clearTimeout(deb);
  }, [clientSearchText, isDecide]);

  // Al abrir: lee minCreditorsRequired del flujo (fallback 2) y fija el mínimo. En create
  // rellena hasta N filas de acreedor de entrada; en decide NO rellena (arranca con una
  // sola fila, useState inicial) para no mostrar filas vacías no-removibles cuando el
  // usuario va a rechazar — reject ignora los acreedores y decide muestra ambos botones,
  // así que la intención no se conoce de antemano. El mínimo real de approve lo hace
  // cumplir el back (400). Corre una vez (montaje); el usuario aún no tipeó, no pisa nada.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      let min = 2;
      try {
        const settings = await getFlowSettings(controller.signal);
        const n = Number(settings?.minCreditorsRequired);
        if (Number.isFinite(n) && n > 0) min = n;
      } catch {
        // getFlowSettings falló → fallback 2 filas.
      }
      setMinCreditors(min);
      if (isDecide) return;
      setCreditors((prev) =>
        prev.length >= min
          ? prev
          : [
              ...prev,
              ...Array.from({ length: min - prev.length }, blankCreditor),
            ]
      );
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Casos del cliente (modo create): al elegir cliente trae sus casos y descarta los de
  // área excluida (SAC/EEPP). Cambiar/limpiar el cliente resetea caso y opciones.
  // AbortController por efecto (mismo patrón que el fetch de montaje de este archivo).
  const selectedClientId = selectedClient?.clientId ?? null;
  useEffect(() => {
    if (isDecide) return undefined;
    setSelectedCase(null);
    setCaseOptions([]);
    if (!selectedClientId) return undefined;
    const controller = new AbortController();
    setLoadingCases(true);
    (async () => {
      try {
        const cases = await getCasesByClientId(
          selectedClientId,
          controller.signal
        );
        setCaseOptions(
          (cases || [])
            .filter((c) => !EXCLUDED_BUSINESS_AREAS.includes(c.businessAreaId))
            .map((c) => ({ label: `${c.code} — ${c.title}`, value: c.caseId }))
        );
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        setCaseOptions([]);
      } finally {
        if (!controller.signal.aborted) setLoadingCases(false);
      }
    })();
    return () => controller.abort();
  }, [selectedClientId, isDecide]);

  const updateCreditor = useCallback((idx, patch) => {
    setCreditors((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    );
  }, []);

  const addCreditor = useCallback(
    () => setCreditors((prev) => [...prev, blankCreditor()]),
    []
  );

  const removeCreditor = useCallback(
    (idx) => {
      setCreditors((prev) =>
        prev.length <= minCreditors ? prev : prev.filter((_, i) => i !== idx)
      );
    },
    [minCreditors]
  );

  // Filas con algún dato: se validan completas (nombre + monto + días).
  const cleanCreditors = useMemo(
    () =>
      creditors
        .map((c) => ({
          creditorName: c.creditorName.trim(),
          debtAmount: c.debtAmount,
          arrearsDays: c.arrearsDays,
        }))
        .filter(
          (c) => c.creditorName || c.debtAmount !== '' || c.arrearsDays !== ''
        ),
    [creditors]
  );

  const creditorsValid =
    cleanCreditors.length > 0 &&
    cleanCreditors.every(
      (c) => c.creditorName && c.debtAmount !== '' && c.arrearsDays !== ''
    );

  const toPayloadCreditors = () =>
    cleanCreditors.map((c) => ({
      creditorName: c.creditorName,
      debtAmount: Number(c.debtAmount),
      arrearsDays: Number(c.arrearsDays),
    }));

  // Reject acepta acreedores opcionales (solo historia); approve/create los exige.
  const isReject = isDecide && decision === 'reject';
  // En create, si el cliente tiene casos elegibles el caso es obligatorio; sin casos
  // elegibles se permite enviar sin caso (el back auto-crea).
  const caseRequired = !isDecide && caseOptions.length > 0;
  // Aprobar exige acreedores válidos (y cliente+teléfono+caso-si-corresponde en create);
  // Rechazar solo no estar enviando.
  const approveDisabled =
    sending ||
    !creditorsValid ||
    (!isDecide && (!selectedClient || !telefono.trim())) ||
    (caseRequired && !selectedCase);

  // Aprobar (create o decide): valida acreedores y abre el confirm en modo approve.
  const submitApprove = useCallback(() => {
    if (cleanCreditors.length > 0 && !creditorsValid) {
      setError(t('proc_required_error'));
      return;
    }
    setError(null);
    setDecision('approve');
    setConfirmOpen(true);
  }, [cleanCreditors.length, creditorsValid, t]);

  // Rechazar (solo decide): acreedores opcionales, razón opcional; abre el confirm en reject.
  const submitReject = useCallback(() => {
    setError(null);
    setDecision('reject');
    setConfirmOpen(true);
  }, []);

  const doSubmit = useCallback(async () => {
    // Se dispara al confirmar en el ConfirmModal anidado: cierra ese confirm y el loader
    // aparece en el cuerpo del modal PRINCIPAL (body-swap por `status`).
    setConfirmOpen(false);
    setStatus('loading');
    setError(null);
    setSubmitError(null);
    try {
      // clientNotificationStatus de la respuesta: 'failed' = la transición SÍ se aplicó
      // pero el WhatsApp al cliente no salió (queda encolado) → estado warning en vez de
      // success. 'sent'/'skipped'/null → success normal. El reject es silencioso por
      // diseño (no hay mensaje que pueda fallar), así que nunca warnea.
      let notificationStatus = null;
      if (isDecide) {
        const payload = { decision };
        if (isReject) {
          payload.rejectionReason = rejectionReason.trim() || undefined;
          if (cleanCreditors.length > 0)
            payload.creditors = toPayloadCreditors();
        } else {
          payload.creditors = toPayloadCreditors();
        }
        const res = await decideProcessViability(checkId, payload);
        if (!isReject)
          notificationStatus = res?.clientNotificationStatus ?? null;
      } else {
        await createProcess({
          clientId: selectedClient.clientId,
          caseId: selectedCase?.value ?? null,
          telefono: telefono.trim(),
          creditors: toPayloadCreditors(),
        });
      }
      setStatus(notificationStatus === 'failed' ? 'warning' : 'success');
    } catch (err) {
      setSubmitError(
        err.response?.data?.message || err.message || t('viab_error_generic')
      );
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDecide,
    isReject,
    decision,
    rejectionReason,
    cleanCreditors,
    checkId,
    selectedClient,
    selectedCase,
    telefono,
    t,
  ]);

  const confirmMessage = isReject
    ? t('proc_confirm_reject_message')
    : isDecide
      ? t('proc_confirm_approve_message')
      : t('proc_confirm_create_message');

  const creditorsBlock = (
    <div className='d-flex flex-column gap-2'>
      <label className='form-label fw-semibold mb-0' style={{ fontSize: 13 }}>
        {t('proc_creditors_title')}
      </label>
      <div style={CREDITORS_CARD}>
        {/* Encabezados de columna (los Input de cada fila van sin label individual) */}
        <div style={{ ...CREDITORS_GRID, padding: '9px 16px 4px' }}>
          <span />
          <span style={CREDITORS_HDR}>{t('proc_creditor_name')}</span>
          <span style={CREDITORS_HDR}>{t('proc_debt_amount')}</span>
          <span style={CREDITORS_HDR}>{t('proc_arrears_days')}</span>
          <span />
        </div>

        {/* Filas numeradas con borde inferior */}
        {creditors.map((c, idx) => (
          <div
            key={idx}
            style={{
              ...CREDITORS_GRID,
              padding: '8px 16px',
              borderTop: '1px solid #f4f5f7',
            }}
          >
            <span style={CREDITORS_NUM}>{idx + 1}</span>
            {/* Nombre del acreedor: Input del DS, igual que Monto y Días. Antes era un
              TextArea autoGrow, pero su bloque de contador/descripción (siempre presente,
              aunque vacío) le sumaba alto y descuadraba la fila. */}
            {Input ? (
              <Input
                value={c.creditorName}
                onChange={(e) =>
                  updateCreditor(idx, { creditorName: readVal(e) })
                }
                placeholder={t('proc_creditor_name')}
                disabled={sending}
                maxLength={256}
                width='100%'
              />
            ) : (
              <input
                className='form-control form-control-sm'
                placeholder={t('proc_creditor_name')}
                maxLength={256}
                value={c.creditorName}
                onChange={(e) =>
                  updateCreditor(idx, { creditorName: e.target.value })
                }
              />
            )}
            {Input ? (
              <Input
                value={formatPesos(c.debtAmount)}
                onChange={(e) =>
                  updateCreditor(idx, { debtAmount: onlyDigits(readVal(e)) })
                }
                placeholder={t('proc_debt_amount')}
                disabled={sending}
                inputMode='numeric'
                width='100%'
              />
            ) : (
              <input
                className='form-control form-control-sm'
                placeholder={t('proc_debt_amount')}
                inputMode='numeric'
                value={formatPesos(c.debtAmount)}
                onChange={(e) =>
                  updateCreditor(idx, {
                    debtAmount: onlyDigits(e.target.value),
                  })
                }
              />
            )}
            {/* Texto + inputMode numérico (igual que Monto), NO type='number': los spinners
              del navegador y su rueda del mouse cambiaban el valor sin querer. El saneo
              real lo hace onlyDigits, así que el estado sigue siendo solo dígitos. */}
            {Input ? (
              <Input
                value={c.arrearsDays}
                onChange={(e) =>
                  updateCreditor(idx, { arrearsDays: onlyDigits(readVal(e)) })
                }
                placeholder={t('proc_arrears_days')}
                disabled={sending}
                inputMode='numeric'
                width='100%'
              />
            ) : (
              <input
                className='form-control form-control-sm'
                placeholder={t('proc_arrears_days')}
                inputMode='numeric'
                value={c.arrearsDays}
                onChange={(e) =>
                  updateCreditor(idx, {
                    arrearsDays: onlyDigits(e.target.value),
                  })
                }
              />
            )}
            {/* Mismo truco que el número: altura del Input y centrado, para que la ✕ no
              quede pegada arriba con alignItems:'start'. */}
            <span style={CREDITORS_ACTION}>
              {creditors.length > minCreditors &&
                (IconButton ? (
                  <IconButton
                    ariaLabel={t('proc_remove_creditor')}
                    icon='close'
                    size='sm'
                    stroke='var(--error-500)'
                    onClick={() => removeCreditor(idx)}
                    disabled={sending}
                  />
                ) : (
                  <button
                    type='button'
                    className='btn btn-link btn-sm text-danger p-0'
                    onClick={() => removeCreditor(idx)}
                    title={t('proc_remove_creditor')}
                  >
                    ✕
                  </button>
                ))}
            </span>
          </div>
        ))}

        {/* Agregar acreedor (debajo, como el "Agregar" de la referencia) */}
        <div
          style={{ padding: '11px 16px 14px', borderTop: '1px solid #f4f5f7' }}
        >
          {Button && (
            <Button
              label={t('proc_add_creditor')}
              size='sm'
              variant='naked'
              onClick={addCreditor}
              disabled={sending}
            />
          )}
        </div>
      </div>
    </div>
  );

  const body = (
    <AsyncStateView
      status={status}
      loadingTitle={t('proc_state_loading')}
      successTitle={t('proc_state_success')}
      warningTitle={t('proc_state_success')}
      warningDescription={t('proc_warning_started_not_notified')}
      errorMessage={submitError}
      // En warning el proceso YA quedó guardado: "Entendido" cierra y refresca (no vuelve
      // al formulario, que sí es lo correcto en error).
      onBack={() =>
        status === 'warning' ? onCompleted?.() : setStatus('idle')
      }
      onSuccessAutoClose={() => onCompleted?.()}
    >
      <div className='d-flex flex-column gap-3'>
        <p className='caption font-light mb-0'>
          {isDecide ? t('proc_decide_help') : t('proc_create_help')}
        </p>

        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Cliente */}
        {isDecide ? (
          <div>
            <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
              {t('proc_client')}
            </label>
            <p className='mb-0' style={{ fontSize: 14 }}>
              {clientLabel || '—'}
            </p>
          </div>
        ) : (
          <>
            <div>
              {ComboBox ? (
                <ComboBox
                  label={t('proc_client')}
                  placeholder={t('proc_client_search_placeholder')}
                  noResultText={t('proc_no_clients')}
                  value={selectedClient}
                  onChange={(option) => {
                    setSelectedClient(option || null);
                    if (option?.phone) setTelefono(option.phone);
                  }}
                  onInputChange={(text) => setClientSearchText(text)}
                  options={clientOptions}
                  isLoading={loadingClients}
                  isClearable
                />
              ) : (
                <>
                  <label
                    className='form-label fw-semibold'
                    style={{ fontSize: 13 }}
                  >
                    {t('proc_client')}
                  </label>
                  <input
                    type='text'
                    className='form-control form-control-sm mb-1'
                    placeholder={t('proc_client_search_placeholder')}
                    value={clientSearchText}
                    onChange={(e) => setClientSearchText(e.target.value)}
                  />
                  {clientOptions.length > 0 && (
                    <ul
                      className='list-group mb-2'
                      style={{
                        fontSize: 13,
                        maxHeight: 200,
                        overflowY: 'auto',
                      }}
                    >
                      {clientOptions.map((c) => (
                        <li
                          key={c.value}
                          className='list-group-item list-group-item-action py-1'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedClient(c);
                            if (c.phone) setTelefono(c.phone);
                          }}
                        >
                          {c.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Caso del cliente: aparece al elegir cliente. Con casos elegibles → ComboBox
              requerido; sin casos → hint y el back auto-crea el caso al aprobar. */}
            {selectedClient && (
              <div>
                {loadingCases || caseOptions.length > 0 ? (
                  ComboBox ? (
                    <ComboBox
                      label={t('proc_case')}
                      placeholder={t('proc_case_placeholder')}
                      noResultText={t('proc_no_clients')}
                      value={selectedCase}
                      onChange={(option) => setSelectedCase(option || null)}
                      options={caseOptions}
                      isLoading={loadingCases}
                      isClearable
                    />
                  ) : (
                    <>
                      <label
                        className='form-label fw-semibold'
                        style={{ fontSize: 13 }}
                      >
                        {t('proc_case')}
                      </label>
                      <select
                        className='form-select form-select-sm'
                        value={selectedCase?.value ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedCase(
                            caseOptions.find((o) => String(o.value) === v) ||
                              null
                          );
                        }}
                      >
                        <option value=''>{t('proc_case_placeholder')}</option>
                        {caseOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </>
                  )
                ) : (
                  <div
                    className='alert alert-info py-2 mb-0'
                    style={{ fontSize: 13 }}
                  >
                    {t('proc_case_autocreate_hint')}
                  </div>
                )}
              </div>
            )}

            <div>
              <label
                className='form-label fw-semibold'
                style={{ fontSize: 13 }}
              >
                {t('proc_phone')}
              </label>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder={t('proc_phone_placeholder')}
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                maxLength={64}
              />
            </div>
          </>
        )}

        {creditorsBlock}

        {/* Motivo del rechazo (solo modo decide): opcional, se usa únicamente al Rechazar. */}
        {isDecide && (
          <TextArea
            label={t('proc_reject_reason')}
            placeholder={t('proc_reject_reason_placeholder')}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(readVal(e))}
            rows={2}
            maxLength={4000}
          />
        )}

        {/* Acciones: una sola fila al pie. Decide → Cancelar · Rechazar · Aprobar; create → Cancelar · Aprobar. */}
        <div className='d-flex justify-content-end gap-2'>
          {Button && (
            <Button
              label={t('viab_cancel')}
              size='sm'
              variant='secondary'
              onClick={onClose}
              disabled={sending}
            />
          )}
          {isDecide && Button && (
            <Button
              label={t('proc_reject')}
              size='sm'
              variant='danger'
              onClick={submitReject}
              disabled={sending}
            />
          )}
          {Button && (
            <Button
              label={t('proc_approve')}
              size='sm'
              variant='primary'
              onClick={submitApprove}
              disabled={approveDisabled}
            />
          )}
        </div>
      </div>
    </AsyncStateView>
  );

  return (
    <>
      {Modal ? (
        <Modal
          title={t('proc_modal_title')}
          show
          onClose={() => {
            if (status === 'loading' || status === 'success') return;
            // Warning: la decisión ya se aplicó → cerrar equivale a completar (refresca
            // la lista del padre en vez de dejarla con la fila vieja).
            if (status === 'warning') {
              onCompleted?.();
              return;
            }
            onClose();
          }}
          width='1000px'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          {body}
        </Modal>
      ) : (
        <div
          role='dialog'
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            zIndex: 9500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}
          onClick={onClose}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 20,
              width: 'min(780px, 94vw)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <h5>{t('proc_modal_title')}</h5>
            {body}
          </div>
        </div>
      )}

      {confirmOpen && (
        <ConfirmModal
          Modal={Modal}
          show
          title={t('proc_confirm_title')}
          message={confirmMessage}
          confirmLabel={isReject ? t('proc_reject') : t('proc_approve')}
          cancelLabel={t('cfg_cancel')}
          confirmVariant={isReject ? 'danger' : 'primary'}
          onConfirm={doSubmit}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}
